import type { Course } from '@/domain/entities/course.entity'
import type {
  CourseDetailsDisplay,
  CourseDetailsAuthorDisplay,
  MapCourseDetailsOptions,
} from '@/domain/entities/course-details.entity'
import { mapCourseDtoToEntity, mapCourseToCurriculumOverviewParts } from './course.mapper'
import { mapLessonDtoArrayToDisplay, mapLessonsToCurriculumSections } from './lesson.mapper'
import type { LessonDisplayDto } from './lesson.mapper'
import type { CourseDetailsResponseDto } from '../dto/course.dto'
import type { LessonDto } from '../dto/lesson.dto'

/**
 * Дочерний маппер: Course → цели курса (courseObjectives из course_objectives с бэка или fallback).
 */
export function mapCourseToLearningObjectives(
  course: Course | null,
  defaultObjectives: string[],
): string[] {
  // Проверяем courseObjectives (из course_objectives)
  if (course?.courseObjectives && Array.isArray(course.courseObjectives) && course.courseObjectives.length > 0) {
    return course.courseObjectives
  }
  return defaultObjectives
}

/**
 * Дочерний маппер: Course → данные автора для отображения (или default).
 * Бекенд повертає author як { _id, author_name, author_description }.
 */
export function mapCourseToAuthorDisplay(
  course: Course | null,
  defaultAuthor: CourseDetailsAuthorDisplay,
): CourseDetailsAuthorDisplay {
  const raw = course?.author as Record<string, unknown> | undefined
  if (!raw) return defaultAuthor

  // Формат з landing2-api: { _id, author_name, author_description, author_photo }
  if (raw.author_name && typeof raw.author_name === 'string') {
    return {
      name: raw.author_name,
      description: typeof raw.author_description === 'string' ? raw.author_description : undefined,
      avatar: typeof raw.author_photo === 'string' && raw.author_photo ? raw.author_photo : undefined,
    }
  }

  // Запасний варіант: пряме поле name (старий формат)
  if ('name' in raw && typeof raw.name === 'string') {
    return raw as unknown as CourseDetailsAuthorDisplay
  }

  return defaultAuthor
}

/**
 * Родительский маппер: один ответ бэка (course + lessons) → CourseDetailsDisplay.
 * Применяет дочерние мапперы (course DTO → entity, lessons → sections, overview, learning objectives, author).
 */
export function mapToCourseDetailsDisplay(
  raw: unknown,
  options: MapCourseDetailsOptions,
): CourseDetailsDisplay {
  const dto = raw as CourseDetailsResponseDto | null
  if (!dto?._id) {
    return {
      course: null,
      curriculumOverviewParts: null,
      curriculumSections: [],
      learningObjectives: options.defaultLearningObjectives,
      authorData: options.defaultAuthor,
    }
  }
  const course: Course | null = mapCourseDtoToEntity(dto)
  const lessonDtos: LessonDto[] = dto.lessons ?? []
  const lessonsDisplay: LessonDisplayDto[] = mapLessonDtoArrayToDisplay(lessonDtos)
  const result: CourseDetailsDisplay = {
    course,
    curriculumOverviewParts: mapCourseToCurriculumOverviewParts(course),
    curriculumSections: mapLessonsToCurriculumSections(lessonsDisplay, options.defaultSectionTitle),
    learningObjectives: mapCourseToLearningObjectives(course, options.defaultLearningObjectives),
    authorData: mapCourseToAuthorDisplay(course, options.defaultAuthor),
  }
  return result
}
